// DRAFT — not legal advice. Every factual assertion here must be confirmed by LAA and reviewed by counsel before this site accepts public traffic or runs paid advertising.
import { createFileRoute, Link } from "@tanstack/react-router";

import { seo } from "@/lib/seo";
import { NAP, SHOW_PHONE } from "@/lib/business";
import { LegalPage, LegalSection } from "@/components/legal/LegalPage";

export const Route = createFileRoute("/_site/legal/disclaimer")({
  head: () =>
    seo({
      title: "Disclaimer",
      description:
        "LAA Concierge Consulting is not a lender and is not affiliated with the SBA. What we do and do not guarantee, and what this site is not.",
      path: "/legal/disclaimer",
    }),
  component: Page,
});

function Page() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Disclaimer"
      lastUpdated="4 September 2026"
      intro="Read this before relying on anything published on this site or produced during an engagement."
    >
      <blockquote className="text-body">
        LAA Concierge Consulting, LLC is an independent business consulting firm. We are not a
        lender, a bank, or a loan broker, and we are not affiliated with or endorsed by the U.S.
        Small Business Administration or any government agency. We do not guarantee loan approval,
        grant awards, or any specific funding outcome. Consulting fees are for advisory and
        preparation services only.
      </blockquote>

      <LegalSection title="We are not a lender or a government agency">
        <p>
          LAA does not lend money, hold deposits, underwrite credit, or broker loans. We do not
          receive a commission from any lender for referring you, and we are not paid by any lender
          based on whether your application succeeds.
        </p>
        <p>
          We are not part of, affiliated with, endorsed by, sponsored by, or approved by the U.S.
          Small Business Administration, the Department of the Treasury, any state agency, or any
          other government body. We do not use government logos or seals. Where we reference an SBA
          programme, a grant programme, or a published eligibility rule, we are describing a
          third-party programme we do not control and whose terms can change without notice. SBA
          programme information is available free of charge directly from the SBA and from SBA
          resource partners.
        </p>
        <p>
          What we actually do in a funding engagement is advisory and preparatory: we help you
          understand which programmes appear to fit, assemble and strengthen your application
          package, write your business plan and financial projections, and prepare you for lender
          questions. The decision belongs to the lender or the awarding body.
        </p>
      </LegalSection>

      <LegalSection title="No guaranteed outcomes">
        <p>
          We do not guarantee, and nothing on this site should be read as promising, any of the
          following: approval of a loan, award of a grant, receipt of any amount of capital,
          particular loan terms or interest rates, a specific timeline for a decision, revenue
          growth, cost reduction, headcount savings, efficiency gains, or any other business result
          from adopting artificial intelligence or from any other work we do.
        </p>
        <p>
          Outcomes depend on your business, your financials, your data, your team's willingness to
          change how it works, market conditions, and the independent judgement of third parties.
          Past work for other clients does not predict what will happen for you.
        </p>
      </LegalSection>

      <LegalSection title="This is not legal, tax, accounting, investment, or financial advice">
        <p>
          LAA is a business consulting firm. We are not a law firm, an accounting firm, a registered
          investment adviser, a broker-dealer, an insurance producer, or a licensed financial
          planner, and no one at LAA is acting in any of those capacities when working with you.
        </p>
        <p>
          Nothing on this site or in our deliverables is legal advice, tax advice, accounting
          advice, investment advice, or a recommendation to buy, sell, or hold any security or
          financial product. Entity structure, contract terms, tax treatment, employment
          classification, regulatory compliance, and data protection obligations all require your
          own licensed professionals. Retain them, and give them our work to review before you act
          on it in any of those areas. We are happy to work alongside them.
        </p>
      </LegalSection>

      <LegalSection title="AI output requires human review">
        <p>
          Artificial intelligence systems — including the ones we build, and the ones we use while
          working — produce probabilistic output. They can be fluent and wrong at the same time.
          They can misread a document, invent a detail that was never in the source, reproduce bias
          present in their training data, and behave differently after an underlying model is
          updated by its vendor.
        </p>
        <p>
          Every AI-assisted deliverable is reviewed by a consultant before it reaches you, and we
          design systems with checkpoints where a person can catch an error. Neither of those makes
          the output infallible. You remain accountable for decisions made using any system we build
          or recommend, and for the consequences of those decisions. Do not use an AI system to make
          a final determination about a person's credit, employment, housing, insurance, or legal
          position without qualified human review and appropriate legal advice — several of those
          uses are separately regulated.
        </p>
      </LegalSection>

      <LegalSection title="Figures, examples, and comparisons are illustrative">
        <p>
          Numbers, ranges, timelines, sample outputs, screenshots, diagrams, and before-and-after
          comparisons shown on this site are illustrative and are intended to show the shape of the
          work, not to predict your result. Where a figure is drawn from a documented source or from
          measured client data, we state the basis alongside it. If no basis is stated, treat the
          figure as illustrative. Any client example is published with permission, and details may
          be generalised or anonymised to protect confidentiality.
        </p>
      </LegalSection>

      <LegalSection title="Third-party links and tools">
        <p>
          This site links to third-party websites, and our recommendations may name third-party
          software, platforms, and service providers. Those links and mentions are for convenience
          and information. They are not endorsements, we do not control those parties, we are not
          responsible for their content, availability, pricing, security, or privacy practices, and
          we receive no commission for naming them unless we disclose it to you in writing. Review
          their terms yourself before you commit.
        </p>
      </LegalSection>

      <LegalSection title="Accuracy and currency of this site">
        <p>
          We try to keep this site accurate and current, but programme rules, technology, pricing,
          and best practice all change. Content is provided as-is, without warranty of accuracy or
          completeness, and may be out of date by the time you read it. Do not rely on general
          website content as a substitute for advice about your specific circumstances — ask us, or
          ask your own adviser.
        </p>
        <p>
          Questions about anything here go to <a href={`mailto:${NAP.email}`}>{NAP.email}</a>
          {SHOW_PHONE ? (
            <>
              {" or "}
              <a href={NAP.phoneHref}>{NAP.phone}</a>
            </>
          ) : null}
          . This disclaimer sits alongside our <Link to="/legal/terms">Terms of Service</Link> and{" "}
          <Link to="/legal/privacy">Privacy Policy</Link>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
